import { z } from 'zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/button';
import { TextInput } from '@/components/inputs/text-input';
import { Form } from '@/components/form';
import { Link, Navigate, useLocation, useNavigate } from '@/util/react-router-dom';
import { CardWrapper } from '@/components/card-wrapper';
import { isValidCode, zt } from '@/util/validators';
import { useTranslation } from '@/contexts/i18n-context';
import styles from './verify-identity-page.module.css';
import { UserEntryLayout } from '@/components/auth/user-entry-layout';
import { TwoFactorMethod, VerifyCodeErrorResponse, VerifyCodeErrorType } from '@/api/api.schemas';
import clsx from 'clsx';
import { useTfaVerify } from '@/api/verify-tfa';
import { useCurrentUser } from '@/contexts/current-user-context';
import { useTfaRequest } from '@/api/request-tfa-code';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Banner } from '@/components/banner';
import { useBranding } from '@/outlets/branding-outlet';
import { EmailSvg } from '@/components/svgs/email';
import { PhoneSvg } from '@/components/svgs/phone';
import { ResetPasswordLink } from '@/components/auth/reset-password-link';

const schema = z.object({
  code: z.string().refine(isValidCode, {
    message: zt('verifyIdentity.validationError'),
  }),
});

type FormValues = z.infer<typeof schema>;

interface VerifyIdentityState {
  method: TwoFactorMethod;
  target: string;
  to?: string;
  availableTfaMethods: TwoFactorMethod[];
}

export function VerifyIdentityPage() {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });
  const navigate = useNavigate();
  const state = useLocation().state as VerifyIdentityState;
  const { setUserData } = useCurrentUser();
  const { refetch } = useBranding();

  const [globalError, setGlobalError] = useState<ReactNode>();

  const { mutate: verifyToken, isPending } = useTfaVerify({
    mutation: {
      onMutate: () => {
        setGlobalError(undefined);
        form.clearErrors('code');
      },
      onSuccess: (data) => {
        setUserData(data.token);
        refetch();
        navigate('/');
      },

      onError: (error: VerifyCodeErrorResponse) => {
        const lowRemainingAttempts =
          error.remainingAttempts != null &&
          (error.remainingAttempts === 1 || error.remainingAttempts === 2);

        switch (error.type) {
          case VerifyCodeErrorType.InvalidCode:
            form.setError('code', { message: t('verifyIdentity.invalidError') });
            setGlobalError(
              lowRemainingAttempts
                ? t('login.errors.lockWarning', {
                    resetPasswordLink: <ResetPasswordLink key="reset-password-link" />,
                  })
                : undefined,
            );
            break;
          case VerifyCodeErrorType.AccountLocked:
            setGlobalError(
              t('login.errors.accountLocked', {
                resetPasswordLink: <ResetPasswordLink key="reset-password-link" />,
              }),
            );
            break;
          default:
            setGlobalError(t('common.errors.unknown'));
        }
      },
    },
  });

  const { mutate: resendCode, isPending: isResendingCode } = useTfaRequest({
    mutation: {
      onSuccess: (data) => {
        if (data.method !== state.method) {
          navigate('/verify-identity', {
            state: { ...state, method: data.method, target: data.destination },
          });
        }
      },
      onError: () => {
        setGlobalError(t('common.errors.unknown'));
      },
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    verifyToken({ data });
  };

  if (!state) {
    return <Navigate to="/login" replace />;
  }

  const { method, target, availableTfaMethods } = state;

  const isEmail = method === TwoFactorMethod.Email;
  const isPhoneAvailable = availableTfaMethods?.includes(TwoFactorMethod.Phone);

  const translationBase = isEmail ? 'verifyIdentity.email' : 'verifyIdentity.phone';

  return (
    <UserEntryLayout>
      <CardWrapper className={styles.wrapper}>
        {!!globalError && (
          <Banner type="error" className={styles.banner}>
            {globalError}
          </Banner>
        )}
        {isEmail ? <EmailSvg /> : <PhoneSvg />}
        <h1 className={clsx('h3', styles.title)}>{t('verifyIdentity.pageTitle')}</h1>
        <div className={clsx(styles.description)}>
          <p>
            {t('verifyIdentity.description', {
              destination: (
                <span key="destination" className={styles.destination}>
                  {target}
                </span>
              ),
            })}
          </p>
          <p>
            {t(`${translationBase}.resendDescription`, {
              link: (
                <Button
                  key="resend-code-button"
                  variant="link"
                  className={styles.resendCodeButton}
                  onClick={() => resendCode({ data: { method } })}
                  disabled={isResendingCode}
                >
                  {t('verifyIdentity.resendCode')}
                </Button>
              ),
            })}
          </p>
        </div>
        <Form className={styles.form} form={form} onSubmit={onSubmit}>
          <TextInput
            trimWhiteSpaces
            placeholder={t('verifyIdentity.placeholder')}
            name="code"
            aria-label={t('verifyIdentity.placeholder')}
            autoComplete="off"
          />
          <Button fullWidth loading={isPending} type="submit">
            {t('verifyIdentity.submit')}
          </Button>
          <p className={styles.contactUs}>
            {t('verifyIdentity.contactUsDescription', {
              link: (
                <Link to="/support" key="contact-us-button">
                  {t('verifyIdentity.contactUs')}
                </Link>
              ),
            })}
          </p>
        </Form>
      </CardWrapper>
      {isPhoneAvailable ? (
        <Button
          variant="link"
          color="accent"
          className={styles.changeMethodButton}
          onClick={() => resendCode({ data: { method: switchMethod(method) } })}
          disabled={isResendingCode}
        >
          {t(`${translationBase}.changeMethod`)}
        </Button>
      ) : (
        <p className={styles.enableSmsHint}>{t('verifyIdentity.enableSmsAfterSignIn')}</p>
      )}
    </UserEntryLayout>
  );
}

function switchMethod(method: TwoFactorMethod) {
  return method === TwoFactorMethod.Email ? TwoFactorMethod.Phone : TwoFactorMethod.Email;
}
